import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { deleteParamsApi, getParamsApi, saveParamsApi } from '../api/wellApi';
import { calculateEur, calculateMultiSegmentForecast, updateMultiSegmentForecast } from '../helpers/calculateForecast';
import { calculateWaterfloodForecast } from '../helpers/calculateWaterfloodForecast';

export const getParams = createAsyncThunk(
  'wells/getParams',
  async (payload, { rejectWithValue }) => {
    try {
      let well = {...payload.well};
      if (well) {
        const params = await getParamsApi(well?.uwi, payload.email);
        well.params = params;
        const fluids = well?.production?.fluids;
        for (let fluid of fluids) {
          const result = await calculateMultiSegmentForecast(well, fluid);
          if (result && Object.keys(result)?.length > 0) {
            let wellForecast = {...well.forecast};
            wellForecast.byFluid = {...wellForecast.byFluid, [fluid]: result.forecast};
            well.forecast = wellForecast;
            let wellEur = {...well.eur};
            wellEur.byFluid = {...wellEur.byFluid, [fluid]: result.eur};
            well.eur = wellEur;
          };
        };
        return well;
      };
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const saveParams = createAsyncThunk(
  'wells/saveParams',
  async (payload, { rejectWithValue }) => {
    try {

      const { uwi, success, date } = await saveParamsApi(payload.well, payload.email);

      return { uwi: uwi, success: success, date: date };
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const deleteParams = createAsyncThunk(
  'wells/deleteParams',
  async (payload, { rejectWithValue }) => {
    try {

      const success = await deleteParamsApi(payload.uwi, payload.email);
      return success;
      
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const getForecast = createAsyncThunk(
  'wells/getForecast',
  async (wellPayload, { rejectWithValue }) => {
    try {
      let well = {...wellPayload};
      
      if (well) {
        const fluids = Object.keys(well.params);
        for (let fluid of fluids) {
          if (well.params?.[fluid] && Object.keys(well.params?.[fluid])?.length > 0 && well.production?.byFluid?.[fluid]?.length > 0) {

            const result = await calculateMultiSegmentForecast(well, fluid);

            if (result && Object.keys(result)?.length > 0) {
              well.params = {...well.params, [fluid]: result.newFluidParams};
              if (fluid === 'oil' && well.params.oil) {
                well.params.oil = {...well.params.oil, useWaterflood: false};
              };
              let wellForecast = {...well.forecast};
              wellForecast.byFluid = {...wellForecast.byFluid, [fluid]: result.forecast};
              well.forecast = wellForecast;
              let wellEur = {...well.eur};
              wellEur.byFluid = {...wellEur.byFluid, [fluid]: result.eur};
              well.eur = wellEur;
            };
          };
        };
      
        return well;
      };

    } catch (error) {
      console.log(error);
      return rejectWithValue(wellPayload?.uwi);
    };
  }
);

export const getWaterfloodForecast = createAsyncThunk(
  'wells/getWaterfloodForecast',
  async (well, { rejectWithValue }) => {
    try {
      const production = well.production;
      const params = well.params.waterflood;

      if (production && params) {
        
        const { oilForecast, eur } = await calculateWaterfloodForecast(production, params);
        
        return {...well, forecast: {byFluid: { oil: oilForecast }}, eur: {byFluid: { oil: eur }}, params: {...well.params, waterflood: params }};
      };

    } catch (error) {
      return rejectWithValue(well?.uwi);
    };
  }
);

export const updateMultiSegmentForecastThunk = createAsyncThunk(
  'wells/updateMultiSegmentForecastThunk',
  async (payload, { rejectWithValue }) => {
    try {
      let well = {...payload.well};
      const fluid = payload.fluid;
      const param = payload.param;
      const value = payload.value;
      const segment = payload.segment;
      const result = await updateMultiSegmentForecast(well, fluid, param, value, segment);
      
      if (result && Object.keys(result)?.length > 0) {
        well.params = {...well.params, [fluid]: result.newFluidParams};
        well.forecast = {...well.forecast, byFluid: {...well.forecast.byFluid, [fluid]: result.forecast}};
        well.eur = {...well.eur, byFluid: {...well.eur.byFluid, [fluid]: result.eur}};
      };
      return well;
    } catch (error) {
      return rejectWithValue(payload?.well?.uwi);
    };
  }
);

export const updateCutoffThunk = createAsyncThunk(
  'wells/updateCutoffThunk',
  async (payload, { rejectWithValue }) => {
    try {
      let well = {...payload.well};
      const fluid = payload.fluid;
      const cutoff = payload.cutoff;

      const eur = calculateEur(well?.forecast?.byFluid?.[fluid], well?.production?.byFluid?.[fluid], cutoff, well?.params?.[fluid]?.ti);
      well.params = {...well.params, cutoff: cutoff};
      well.eur = {...well.eur, byFluid: {...well.eur.byFluid, [fluid]: eur}};

      return well;
    } catch (error) {
      return rejectWithValue(payload?.well?.uwi);
    };
  }
);

export const wellSlice = createSlice({
  name: 'wells',
  initialState: {
    byUwi: {
      
    },
    allUwis: [],
    currentWellUwi: "",
  },
  reducers: {
    addWell: (state, action) => {
      const wellUwi = action.payload.well.uwi;
      if (!state.allUwis.includes(wellUwi)) {
        state.byUwi[wellUwi] = {...action.payload.well, checked: true};
        state.allUwis.push(wellUwi);
        state.currentWellUwi = wellUwi;
      };
    },
    addWells: (state, action) => {
      try {
        const wellUwis = [...action.payload.uwis];
        const wells = action.payload.wells;
        wellUwis?.forEach(uwi => {
          state.byUwi[uwi] = {...wells[uwi], checked: true};
          if (state.allUwis.indexOf(uwi) === -1) {
            state.allUwis.push(uwi);
          };
        });
        state.currentWellUwi = wellUwis[wellUwis.length - 1];
      } catch (error) {
        return;
      };
    },
    setWells: (state, action) => {
      try {
        const wells = action.payload.wells;
        const allUwis = wells.allUwis;
        allUwis.forEach(uwi => {
          state.byUwi[uwi] = {...wells.byUwi[uwi], checked: true};
          if (state.allUwis.indexOf(uwi) === -1) {
            state.allUwis.push(uwi);
          };
        });
        state.currentWellUwi = wells.currentWellUwi;
      } catch (error) {
        return;
      };
    },
    deleteWell: (state, action) => {
      const wellUwi = action.payload.wellUwi;
      delete state.byUwi[wellUwi];
      state.allUwis = state.allUwis.filter(uwi => uwi !== wellUwi);
    },
    deleteWells: (state, action) => {
      const uwis = action.payload.uwis;
      for (let uwi of uwis) {
        delete state.byUwi[uwi];
      };
      state.allUwis = state.allUwis.filter(uwi => uwis.indexOf(uwi) === -1);
      if (uwis.indexOf(state.currentWellUwi) !== -1) {
        state.currentWellUwi = state.allUwis[0];
      };
    },
    makeWellCurrent: (state, action) => {
      const wellUwi = action.payload.wellUwi;
      state.currentWellUwi = wellUwi;
    },
    updateWell: (state, action) => {
      const wellUwi = action.payload.uwi;
      const production = action.payload.production;
      const params = action.payload.params;
      const details = action.payload.details;
      const forecast = action.payload.forecast;
      const status = action.payload.status;
      const lastModified = action.payload.lastModified;

      if (production) state.byUwi[wellUwi].production = production;
      if (params) state.byUwi[wellUwi].params = params;
      if (details) state.byUwi[wellUwi].details = details;
      if (forecast) state.byUwi[wellUwi].forecast = forecast;
      if (status) state.byUwi[wellUwi].status = status;
      if (lastModified) state.byUwi[wellUwi].lastModified = lastModified;

    },
    updateWellStatus: (state, action) => {
      const uwi = action.payload.uwi;
      const status = action.payload.status;
      const details = action.payload.details || {};

      if (state.byUwi[uwi]) {
        state.byUwi[uwi].status = status;
        state.byUwi[uwi].details = details;
        if (status === 'failed' || status === 'non-producing') {
          state.byUwi[uwi].checked = false;
        };
      };
    },
    updateWellsStatus: (state, action) => {
      const uwis = action.payload.uwis;
      uwis.forEach(uwi => {
        if (state.byUwi[uwi]) {
          state.byUwi[uwi].status = action.payload.status;
        };
      });
    },
    toggleCheckWell: (state, action) => {
      const wellUwi = action.payload.wellUwi;
      state.byUwi[wellUwi].checked = !state.byUwi[wellUwi].checked;
    },
    toggleCheckAllWells: (state, action) => {
      const wellUwis = action.payload.wellUwis;
      wellUwis.forEach(uwi => {
        if (state.byUwi[uwi]) {
          state.byUwi[uwi].checked = true;
        };
      });
    },
    toggleUncheckAllWells: (state, action) => {
      const wellUwis = action.payload.wellUwis;
      wellUwis.forEach(uwi => {
        if (state.byUwi[uwi]) {
          state.byUwi[uwi].checked = false;
        };
      });
    },
    toggleWellVisibility: (state, action) => {
      const wellUwi = action.payload;
      state.byUwi[wellUwi].visible = !state.byUwi[wellUwi].visible;
    },
    updateParam: (state, action) => {
      try {
        const wellUwi = action.payload.uwi;
        const fluid = action.payload.fluid;
        const param = action.payload.param;
        const value = action.payload.value;
        if (!state.byUwi[wellUwi].params[fluid]) {
          state.byUwi[wellUwi].params = {...state.byUwi[wellUwi].params, [fluid]: {[param]: value}};
        } else {
          state.byUwi[wellUwi].params[fluid][param] = value;
        };
      } catch (error) {
        console.log(error);
      };
    },
    updateParams: (state, action) => {
      try {
        const wellUwi = action.payload.uwi;
        const params = action.payload.params;
        state.byUwi[wellUwi].params = params;
      } catch (error) {
        console.log(error);
      };
    },
    stopProcessing: (state, action) => {
      state.allUwis.forEach(uwi => {
        if (state.byUwi[uwi].status === "processing") {
          state.byUwi[uwi].status = 'idle';
        };
      });
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getForecast.fulfilled, (state, action) => {
      try {
        const uwi = action.payload.uwi;
        if (action?.payload?.production && Object.keys(action?.payload?.production)?.length > 0) {
          state.byUwi[uwi] = {...action.payload, status: 'processed', checked: true};
        } else {
          state.byUwi[uwi] = {...action.payload, status: 'non-producing', checked: false};
        };
        if (!state.currentWellUwi || !state.currentWellUwi.length > 0 || state.byUwi[state.currentWellUwi]?.status !== 'processed') {
          state.currentWellUwi = uwi;
        };
      } catch (error) {
        console.log(error);
        return;  
      };
    })
    builder.addCase(getForecast.rejected, (state, action) => {
      try {
        const uwi = action.meta.arg.uwi;
        state.byUwi[uwi].status = 'failed';
        state.byUwi[uwi].checked = false;
      } catch (error) {
        console.log(error);
        return;
      };
    })
    builder.addCase(getWaterfloodForecast.fulfilled, (state, action) => {
      try {
        const well = action.payload;
        const uwi = well.uwi;
        state.byUwi[uwi] = {...well, status: 'processed', checked: true};
      } catch (error) {
        console.log(error);
        return;  
      };
    })
    builder.addCase(getParams.fulfilled, (state, action) => {
      const uwi = action.payload.uwi;
      state.byUwi[uwi] = {...action.payload, status: 'processed', checked: true};
      if (!state.currentWellUwi || !state.currentWellUwi.length > 0 || state.byUwi[state.currentWellUwi]?.status !== 'processed') {
        state.currentWellUwi = uwi;
      };
    })
    builder.addCase(getParams.rejected, (state, action) => {
      try {
        const uwi = action.meta.arg.well.uwi;
        state.byUwi[uwi].status = 'failed';
        state.byUwi[uwi].checked = false;
      } catch (error) {
        return;
      };
    })
    builder.addCase(updateMultiSegmentForecastThunk.fulfilled, (state, action) => {
      const uwi = action.payload.uwi;
      state.byUwi[uwi] = {...action.payload, status: 'processed', checked: true};
      if (!state.currentWellUwi || !state.currentWellUwi.length > 0 || state.byUwi[state.currentWellUwi]?.status !== 'processed') {
        state.currentWellUwi = uwi;
      };
    })
    builder.addCase(updateMultiSegmentForecastThunk.rejected, (state, action) => {
      const uwi = action.meta.arg.well.uwi;
      state.byUwi[uwi].status = 'failed';
      state.byUwi[uwi].checked = false;
    })
    builder.addCase(updateCutoffThunk.fulfilled, (state, action) => {
      const uwi = action.payload.uwi;
      state.byUwi[uwi] = action.payload;
    })
    builder.addCase(saveParams.fulfilled, (state, action) => {
      try {
        const uwi = action.payload.uwi;
        const success = action.payload.success;
        const date = action.payload.date;

        if (success && state.byUwi[uwi]) {
          state.byUwi[uwi].lastModified = date;
        };
      } catch (error) {
        console.log(error);
      };
    })
  },
})

export const { 
  addWell,
  addWells,
  setWells,
  deleteWell, 
  deleteWells, 
  makeWellCurrent, 
  updateWell, 
  updateWellStatus, 
  updateWellsStatus, 
  toggleCheckWell,
  toggleCheckAllWells, 
  toggleUncheckAllWells, 
  toggleWellVisibility, 
  updateParam,
  updateParams,
  stopProcessing
} = wellSlice.actions;

export const selectWells = (state) => state.wells;

export default wellSlice.reducer;